import { html, when, ref } from '@microsoft/fast-element';
import { backIcon, executeIcon } from '../../../../styles';
import {
  formatCodDate,
  formatDateFullMonthName,
  getEnumReadableValue,
  getFormattedNumToFixedDecimals,
} from '../../../../utils/formatting';
import type { ExecutionDrilldown } from './execution-drilldown';
import { hasOnlyTransactionAdvisor } from '../../../../utils/profiles';
import {
    ARTERIA_ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS,
    CONTRACT_TYPE,
    FORM_AGREEMENT_TYPE,
    NEGOTIATION_STATUS,
} from '../../../../utils';

export const ExecutionDrilldownTemplate = html<ExecutionDrilldown>`
  ${when(
    (x) => x.negotiation,
    html<ExecutionDrilldown>`
      <div
        ${ref('drilldownWrapper')}
        class="drilldown-wrapper ${(x) => (x.isNegotiationDetails ? 'isNegotiationDetails' : '')}"
      >
        <div
          class="header-wrapper"
          style="display: ${(x) => (x.isNegotiationDetails ? 'none' : 'flex')}"
        >
          <div class="vppa-ticker">
            ${(x) =>
              x.isAutoExecuted
                ? x.autoExecutedNegotiation.VPPA_TICKER
                : x.arteriaNegotiation.TICKER}
          </div>
        </div>
        ${when(
          (x) => x.isAutoExecuted,
          html<ExecutionDrilldown>`
            <div class="table-contents">
              <div class="header-column">
                <div>Unique Transaction Identifier (UTI)</div>
                <div>Unique Product Identifier (UPI)</div>
                <div>Transaction ID</div>
                <div>VPPA Unique ID</div>
                <div>Bid Unique ID</div>
                <div>Trade Date</div>
                <div>Form Agreement Type</div>
                <div>Contract Type</div>
                <div>Buyer</div>
                <div>Seller</div>
                <div>Reporting Counterparty</div>
                <div>Project Name</div>
                <div>Technology Type</div>
                <div>Location</div>
                <div>ISO/RTO</div>
                <div>Settlement Point</div>
                <div>Settlement Point Index</div>
                <div>Floating Price</div>
                <div>Equipment Selection</div>
                <div>Interconnection Point</div>
                <div>Interconnecting Utility</div>
                <div>Nameplate Capacity</div>
                <div>Buyer's Fraction</div>
                <div>Buyer's Capacity</div>
                <div>Tenor Start Date</div>
                <div>Tenor (months)</div>
                <div>Fixed Price</div>
                <div>Applicable REC Program</div>
                <div>Applicable Tracking System</div>
                <div>REC delivery Methodology</div>
              </div>
              <div class="value-column" ${ref('valueColumnRef')}>
                <div>${(x) => x.autoExecutedNegotiation?.UNIQUE_TRANSACTION_IDENTIFIER}</div>
                <div>${(x) => x.autoExecutedNegotiation?.UPI}</div>
                <div>${(x) => x.autoExecutedNegotiation?.UNIQUE_ID}</div>
                <div>${(x) => x.autoExecutedNegotiation?.VPPA_UNIQUE_ID}</div>
                <div>${(x) => x.autoExecutedNegotiation?.RFQ_UNIQUE_ID}</div>
                <div>${(x) => formatCodDate(x.autoExecutedNegotiation?.CREATED_AT)}</div>
                <div>${(x) => x.autoExecutedNegotiation?.GOVERNING_AGREEMENT}</div>
                <div>
                  ${(x) =>
                    getEnumReadableValue(
                      x.contractTypeEnum,
                      x.autoExecutedNegotiation?.VPPA_CONTRACT_TYPE
                    )}
                </div>
                <div>${(x) => x.autoExecutedNegotiation?.BUYER_COMPANY_NAME}</div>
                <div>${(x) => x.autoExecutedNegotiation?.SELLER_COMPANY_NAME}</div>
                <div>${(x) => x.autoExecutedNegotiation?.REPORTING_COUNTERPARTY}</div>
                <div>${(x) => x.autoExecutedNegotiation?.PROJECT_NAME}</div>
                <div>
                  ${(x) =>
                    getEnumReadableValue(x.technologyEnum, x.autoExecutedNegotiation?.TECHNOLOGY)}
                </div>
                <div>
                  ${(x) =>
                    x.autoExecutedNegotiation.COUNTRY_NAME === 'United States'
                      ? x.autoExecutedNegotiation?.COUNTY_NAME +
                        ', ' +
                        x.autoExecutedNegotiation?.STATE_NAME
                      : x.autoExecutedNegotiation.COUNTRY_NAME}
                </div>
                <div>${(x) => x.autoExecutedNegotiation?.ISO}</div>
                <div>${(x) => x.autoExecutedNegotiation?.SETTLEMENT_POINT}</div>
                <div>
                  ${(x) =>
                    getEnumReadableValue(
                      x.settlementIndexEnum,
                      x.autoExecutedNegotiation?.SETTLEMENT_INDEX
                    )}
                </div>
                <div>
                  ${(x) =>
                    `${x.autoExecutedNegotiation?.ISO} ${
                      x.autoExecutedNegotiation?.SETTLEMENT_POINT
                    } ${getEnumReadableValue(
                      x.settlementIndexEnum,
                      x.autoExecutedNegotiation?.SETTLEMENT_INDEX
                    )} Index`}
                </div>
                <div>${(x) => x.autoExecutedNegotiation?.MAKE_AND_MODEL}</div>
                <div>${(x) => x.autoExecutedNegotiation?.INTERCONNECTION_POINT}</div>
                <div>${(x) => x.autoExecutedNegotiation?.INTERCONNECTION_UTILITY}</div>
                <div>${(x) => x.autoExecutedNegotiation?.MEGAWATT_AC} MW</div>
                <div>
                  ${(x) =>
                    x
                      .getRatio(
                        x.autoExecutedNegotiation?.OFFERED_CAPACITY,
                        x.autoExecutedNegotiation?.MEGAWATT_AC
                      )
                      .toFixed(2)}%
                </div>
                <div>${(x) => x.autoExecutedNegotiation?.OFFERED_CAPACITY} MW</div>
                <div>${(x) => formatCodDate(x.autoExecutedNegotiation?.TENOR_START_DATE)}</div>
                <div>${(x) => x.autoExecutedNegotiation?.TENOR_MONTHS}</div>
                <div>
                  ${(x) =>
                    getFormattedNumToFixedDecimals(
                      x.autoExecutedNegotiation?.FIXED_PRICE,
                      '',
                      2,
                      '$'
                    )}
                </div>
                <div>
                  ${(x) =>
                    getEnumReadableValue(
                      x.applicableRECProgramEnum,
                      x.autoExecutedNegotiation?.APPLICABLE_REC_PROGRAM
                    )}
                </div>
                <div>
                  ${(x) =>
                    getEnumReadableValue(
                      x.applicableTrackingSystemEnum,
                      x.autoExecutedNegotiation?.APPLICABLE_TRACKING_SYSTEM
                    )}
                </div>
                <div>
                  ${(x) =>
                    getEnumReadableValue(
                      x.recDeliveryMethodologyEnum,
                      x.autoExecutedNegotiation?.REC_DELIVERY_METHODOLOGY
                    )}
                </div>
              </div>
            </div>
            ${when(
              (x) => !x.isNegotiationDetails,
              html`
                ${when(
                  (x) =>
                    x.autoExecutedNegotiation?.GOVERNING_AGREEMENT ===
                    'EXISTING_ISDA_MASTER_AGREEMENT',
                  html<ExecutionDrilldown>`
                    <div class="warning-message">
                      This Mini Post-Build VPPA Confirmation incorporates by reference the
                      Post-Build VPPA Annex, as published by CleanTrade as of the Trade Date,
                       and the REC Annex as defined and incorporated therein.
                       This Mini Post-Build VPPA Confirmation supplements, forms a part of,
                       and is subject to, the ISDA Master Agreement, dated as of
                      ${(x) => formatDateFullMonthName(x.autoExecutedNegotiation?.EFFECTIVE_DATE)},
                      between Buyer and Seller, including the Schedule thereto.
                    </div>
                  `
                )}
                ${when(
                  (x) =>
                    x.autoExecutedNegotiation?.GOVERNING_AGREEMENT !==
                    'EXISTING_ISDA_MASTER_AGREEMENT',
                  html<ExecutionDrilldown>`
                    <div class="warning-message">
                      This Mini Post-Build VPPA Confirmation incorporates by reference the CleanTrade
                      Standalone VPPA Annex, as published by CleanTrade as of the Trade Date.
                    </div>
                  `
                )}
              `
            )}
          `
        )}
        ${when(
          (x) => !x.isAutoExecuted && x.arteriaNegotiation.CONTRACT_TYPE !== CONTRACT_TYPE.REC,
          html<ExecutionDrilldown>`
            <div class="table-contents">
              <div class="header-column">
                <div>Buyer</div>
                <div>Seller</div>
                ${when(
                  (x) => (
                      x.arteriaNegotiation?.CONTRACT_TYPE == CONTRACT_TYPE.POST_BUILD_VPPA ||
                      x.arteriaNegotiation?.CONTRACT_TYPE == CONTRACT_TYPE.NEW_BUILD_VPPA
                    ) &&
                    x.arteriaNegotiation?.NEGOTIATION_STATUS == NEGOTIATION_STATUS.EXECUTED,
                  html`<div>Unique Transaction Identifier (UTI)</div>`)}
                ${when(
                  (x) => x.arteriaNegotiation?.NEGOTIATION_STATUS == NEGOTIATION_STATUS.EXECUTED,
                  html`<div>Unique Product Identifier (UPI)</div>`)}
                <div>Transaction ID</div>
                <div>VPPA Unique ID</div>
                <div>RFQ Unique ID</div>
                <div>Form Agreement Type</div>
                <div>Contract Type</div>
                <div>Project Name</div>
                ${when(
                     (x) => x.arteriaNegotiation?.CONTRACT_TYPE == CONTRACT_TYPE.POST_BUILD_VPPA || x.arteriaNegotiation?.CONTRACT_TYPE == CONTRACT_TYPE.NEW_BUILD_VPPA,
                     html`<div>Reporting Counterparty</div>`)}
                <div>Technology Type</div>
                <div>Project Location</div>
                <div>ISO</div>
                <div>Settlement Point</div>
                <div>Settlement Point Index</div>
                <div>Nameplate Capacity Megawatt (AC)</div>
                <div>Buyer's Fraction</div>
                <div>Tenor Start Date</div>
                <div>Tenor (months)</div>
                <div>Swap Settlement Calculation Method</div>
                ${when(
                  (x) =>
                    x.showValueByArteriaSwapValue(
                      ARTERIA_ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS.FIXED_FOR_FLOATING
                    ),
                  html<ExecutionDrilldown>`
                    <div>Fixed Price</div>
                  `
                )}
                ${when(
                  (x) =>
                    x.showValueByArteriaSwapValue(
                      ARTERIA_ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS.UPDSIDE_SHARING
                    ),
                  html<ExecutionDrilldown>`
                    <div>Upside Sharing Fixed Price</div>
                    <div>Seller-Allocated Upside Percentage</div>
                  `
                )}
                ${when(
                  (x) =>
                    x.showValueByArteriaSwapValue(
                      ARTERIA_ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS.BUYER_UPSIDE_ALLOCATION
                    ),
                  html<ExecutionDrilldown>`
                    <div>Buyer Upside Allocation Price</div>
                    <div>Floor Price</div>
                  `
                )}
                ${when(
                  (x) =>
                    x.showValueByArteriaSwapValue(
                      ARTERIA_ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS.COLLAR
                    ),
                  html<ExecutionDrilldown>`
                    <div>Collar Cap Price</div>
                    <div>Collar Floor Price</div>
                  `
                )}
                <div>Negative Pricing Method</div>
                <div>Applicable REC Program</div>
                <div>Performance Guarantee Method</div>
              </div>
              <div class="value-column" ${ref('valueColumnRef')}>
                <div
                  class="${(x) =>
                    x.isExpiredOrDeleted(x.arteriaNegotiation?.NEGOTIATION_STATUS)
                      ? ''
                      : x.getStatusClassIcon(true)}"
                >
                  ${(x) => x.arteriaNegotiation?.BUYER_COMPANY_NAME}
                </div>
                <div
                  class="${(x) =>
                    x.isExpiredOrDeleted(x.arteriaNegotiation?.NEGOTIATION_STATUS)
                      ? ''
                      : x.getStatusClassIcon(false)}"
                >
                  ${(x) => x.arteriaNegotiation?.SELLER_COMPANY_NAME}
                </div>
                ${when(
                  (x) => (
                      x.arteriaNegotiation?.CONTRACT_TYPE == CONTRACT_TYPE.POST_BUILD_VPPA ||
                      x.arteriaNegotiation?.CONTRACT_TYPE == CONTRACT_TYPE.NEW_BUILD_VPPA
                    ) &&
                    x.arteriaNegotiation?.NEGOTIATION_STATUS == NEGOTIATION_STATUS.EXECUTED,
                    html`<div>${(x) => x.arteriaNegotiation?.UNIQUE_TRANSACTION_IDENTIFIER}</div>`)}
                    ${when(
                      (x) => x.arteriaNegotiation?.CONTRACT_TYPE == CONTRACT_TYPE.POST_BUILD_VPPA || x.arteriaNegotiation?.CONTRACT_TYPE == CONTRACT_TYPE.NEW_BUILD_VPPA,
                    html`<div>${(x) => x.arteriaNegotiation?.UPI}</div>`)}
                <div>${(x) => x.arteriaNegotiation?.UNIQUE_ID}</div>
                <div>${(x) => x.arteriaNegotiation?.VPPA_UNIQUE_ID}</div>
                <div>${(x) => x.arteriaNegotiation?.RFQ_UNIQUE_ID}</div>
                <div>${(x) => x.arteriaNegotiation?.FORM_AGREEMENT_TYPE}</div>
                <div>
                  ${(x) =>
                    getEnumReadableValue(x.contractTypeEnum, x.arteriaNegotiation?.CONTRACT_TYPE)}
                </div>
                <div>${(x) => x.arteriaNegotiation?.NAME}</div>
                ${when(
                  (x) => x.arteriaNegotiation?.CONTRACT_TYPE == CONTRACT_TYPE.POST_BUILD_VPPA || x.arteriaNegotiation?.CONTRACT_TYPE == CONTRACT_TYPE.NEW_BUILD_VPPA,
                    html`<div>${(x) => x.arteriaNegotiation?.REPORTING_COUNTERPARTY}</div>`)}
                <div>
                  ${(x) => getEnumReadableValue(x.technologyEnum, x.arteriaNegotiation?.TECHNOLOGY)}
                </div>
                <div>${(x) => x.arteriaNegotiation?.STATE_NAME}</div>
                <div>${(x) => x.arteriaNegotiation?.ISO}</div>
                <div>
                  ${(x) => x.getDefaultValueIfBlank(x.arteriaNegotiation?.SETTLEMENT_POINT)}
                </div>
                <div>
                  ${(x) =>
                    x.getDefaultValueIfBlank(
                      getEnumReadableValue(
                        x.settlementIndexEnum,
                        x.arteriaNegotiation?.SETTLEMENT_INDEX
                      )
                    )}
                </div>
                <div>${(x) => x.getDefaultValueIfBlank(x.arteriaNegotiation?.MEGAWATT_AC)}</div>
                <div>${(x) => x.getDefaultValueIfBlank(x.arteriaNegotiation?.BUYERS_FRACTION)}</div>
                <div>
                  ${(x) => x.getDefaultValueIfBlank(x.arteriaNegotiation?.TENOR_START_DATE)}
                </div>
                <div>${(x) => x.getDefaultValueIfBlank(x.arteriaNegotiation?.TENOR_MONTHS)}</div>
                <div>
                  ${(x) =>
                    x.getDefaultValueIfInvalidSwap(
                      x.arteriaNegotiation?.ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS
                    )}
                </div>

                ${when(
                  (x) =>
                    x.showValueByArteriaSwapValue(
                      ARTERIA_ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS.FIXED_FOR_FLOATING
                    ),
                  html<ExecutionDrilldown>`
                    <div>
                      ${(x) =>
                        x.validateArteriaSwapString()
                          ? x.arteriaNegotiation?.FIXED_PRICE
                          : x.DEFAULT_ARTERIA_VALUE}
                    </div>
                  `
                )}
                ${when(
                  (x) =>
                    x.showValueByArteriaSwapValue(
                      ARTERIA_ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS.UPDSIDE_SHARING
                    ),
                  html<ExecutionDrilldown>`
                    <div>
                      ${(x) =>
                        x.getDefaultValueIfInvalidSwap(
                          x.arteriaNegotiation?.UPSIDE_SHARING_FIXED_PRICE
                        )}
                    </div>
                    <div>
                      ${(x) =>
                        x.getDefaultValueIfInvalidSwap(
                          x.arteriaNegotiation?.SELLER_ALLOCATED_UPSIDE_PERCENTAGE
                        )}
                    </div>
                  `
                )}
                ${when(
                  (x) =>
                    x.showValueByArteriaSwapValue(
                      ARTERIA_ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS.BUYER_UPSIDE_ALLOCATION
                    ),
                  html<ExecutionDrilldown>`
                    <div>
                      ${(x) =>
                        x.getDefaultValueIfInvalidSwap(
                          x.arteriaNegotiation?.BUYER_UPSIDE_ALLOCATION_PRICE
                        )}
                    </div>
                    <div>
                      ${(x) =>
                        x.getDefaultValueIfInvalidSwap(
                          x.arteriaNegotiation?.BUYER_UPSIDE_ALLOCATION_FLOOR_PRICE
                        )}
                    </div>
                  `
                )}
                ${when(
                  (x) =>
                    x.showValueByArteriaSwapValue(
                      ARTERIA_ALTERNATIVE_SWAP_SETTLEMENT_CALCULATION_METHODS.COLLAR
                    ),
                  html<ExecutionDrilldown>`
                    <div>
                      ${(x) =>
                        x.getDefaultValueIfInvalidSwap(x.arteriaNegotiation?.COLLAR_CAP_PRICE)}
                    </div>
                    <div>
                      ${(x) =>
                        x.getDefaultValueIfInvalidSwap(x.arteriaNegotiation?.COLLAR_FLOOR_PRICE)}
                    </div>
                  `
                )}
                <div>
                  ${(x) =>
                    x.getDefaultValueIfBlank(
                      getEnumReadableValue(
                        x.negativePriceMethodEnum,
                        x.arteriaNegotiation?.NEGATIVE_PRICING_METHOD
                      )
                    )}
                </div>
                <div>
                  ${(x) =>
                    x.getDefaultValueIfBlank(
                      getEnumReadableValue(
                        x.applicableRECProgramEnum,
                        x.arteriaNegotiation?.APPLICABLE_REC_PROGRAM
                      )
                    )}
                </div>
                <div>
                  ${(x) =>
                    x.getDefaultValueIfBlank(
                      getEnumReadableValue(
                        x.performanceGuaranteeMethodEnum,
                        x.arteriaNegotiation?.PERFORMANCE_GUARANTEE_METHOD
                      )
                    )}
                </div>
              </div>
            </div>
            ${when(
              (x) => !x.isNegotiationDetails,
              html`
                ${when(
                  (x) =>
                    !['EXPIRED', 'BUYER_DELETED', 'SELLER_DELETED', 'FULL_DELETED'].includes(
                      x.arteriaNegotiation?.NEGOTIATION_STATUS
                    ),
                  html<ExecutionDrilldown>`
                    <div class="warning-message">
                      This VPPA Transaction will be deemed executed, and will become legally
                      binding, in accordance with its terms upon each party clicking "Execute".
                      <br />
                      <br />
                      Note the Execution Timeclock will expire at 5PM EST today. Further, by
                      clicking "Execute", the action cannot be withdrawn unless the Timeclock
                      expires AND the other party does not Execute.
                    </div>
                  `
                )}
                ${when(
                        (x) =>
                                (!x.arteriaNegotiation?.NOTIONAL_ROUNDED || !x.arteriaNegotiation?.REPORTING_COUNTERPARTY) && (x.arteriaNegotiation?.CONTRACT_TYPE == CONTRACT_TYPE.POST_BUILD_VPPA || x.arteriaNegotiation?.CONTRACT_TYPE == CONTRACT_TYPE.NEW_BUILD_VPPA),
                        html<ExecutionDrilldown>`
                                <div class="warning-message">
                                    ${(x) => x.getNotionalWarningMsg()}
                                </div>
                    `
                )}
                <div
                  class="button-wrapper ${(x) =>
                    ['EXPIRED', 'BUYER_DELETED', 'SELLER_DELETED', 'FULL_DELETED'].includes(
                      x.arteriaNegotiation?.NEGOTIATION_STATUS
                    )
                      ? 'margin-top-long'
                      : ''}"
                >
                  ${when(
                    (x) =>
                      x.arteriaNegotiation?.NEGOTIATION_STATUS === 'PENDING_EXECUTION' ||
                      x.arteriaNegotiation?.NEGOTIATION_STATUS === 'EXECUTED_AWAITING_BUYER' ||
                      x.arteriaNegotiation?.NEGOTIATION_STATUS === 'EXECUTED_AWAITING_SELLER',
                    html<ExecutionDrilldown>`
                      <zero-button
                        :disabled=${(x) => x.disableExecuteBtn()}
                        appearance="danger"
                        @click="${(x) => x.reopenNegotiationConfirm()}"
                      >
                        ${backIcon('start')}Back to Negotiation
                      </zero-button>

                      ${when(
                        (x) => !hasOnlyTransactionAdvisor(x.auth.loggedUserResult),
                        html`
                          <zero-button
                            :disabled=${(x) => x.disableExecuteBtn()}
                            appearance="primary"
                            @click="${(x) => x.executeNegotiation()}"
                          >
                            ${executeIcon('start')}Execute
                          </zero-button>
                        `
                      )}
                    `
                  )}
                  ${when(
                    (x) => x.arteriaNegotiation?.NEGOTIATION_STATUS === 'PENDING_EXECUTION_TIMER',
                    html<ExecutionDrilldown>`
                      <zero-button
                        :disabled=${(x) => x.disableExecuteBtn()}
                        appearance="danger"
                        @click="${(x) => x.reopenNegotiationConfirm()}"
                      >
                        ${backIcon('start')}Back to Negotiation
                      </zero-button>
                      ${when(
                        (x) => !hasOnlyTransactionAdvisor(x.auth.loggedUserResult),
                        html`
                          <zero-button
                            disabled=true
                            appearance="disabled"
                            id="execute-button--pending-execution"
                          >
                            ${executeIcon('start')}Execute
                          </zero-button>
                          <zero-tooltip
                            anchor="execute-button--pending-execution"
                            position="left"
                          >
                            Execution can only occur during Business Hours</br>
                            Monday to Friday from 12AM to 5PM EST
                          </zero-tooltip>
                        `
                      )}
                    `
                  )}
                  ${when(
                    (x) =>
                      ['EXPIRED', 'BUYER_DELETED', 'SELLER_DELETED', 'FULL_DELETED'].includes(
                        x.arteriaNegotiation?.NEGOTIATION_STATUS
                      ),
                    html<ExecutionDrilldown>`
                      <zero-button slot="buttons" appearance="danger" @click="${(x) => x.goToArteriaNegotiationsPage()}">
                        ${backIcon('start')}Return to Contracting Module
                      </zero-button>
                    `
                  )}
                </div>
              `
            )}
          `
        )}
        ${when(
          (x) => !x.isAutoExecuted && x.arteriaNegotiation.CONTRACT_TYPE === CONTRACT_TYPE.REC,
          html<ExecutionDrilldown>`
            <div class="table-contents">
              <div class="header-column">
                <div>Transaction ID</div>
                <div>REC Unique ID</div>
                <div>RFQ Unique ID</div>
                <div>Form Agreement Type</div>
                <div>Contract Type</div>
                <div>Buyer</div>
                <div>Seller</div>
                <div>Project Name</div>
                <div>Technology Type</div>
                <div>Location</div>
                <div>ISO</div>
                <div>Nameplate Capacity</div>
                <div>Buyer's Fraction</div>
                <div>Tenor Start Date</div>
                <div>Tenor (months)</div>
                <div>Fixed Price</div>
                <div>Applicable REC Program</div>
                <div>Performance Guarantee Method</div>
              </div>
              <div class="value-column" ${ref('valueColumnRef')}>
                <div>${(x) => x.arteriaNegotiation?.UNIQUE_ID}</div>
                <div>${(x) => x.arteriaNegotiation?.VPPA_UNIQUE_ID}</div>
                <div>${(x) => x.arteriaNegotiation?.RFQ_UNIQUE_ID}</div>
                <div>
                  ${(x) =>
                    x.arteriaNegotiation?.FORM_AGREEMENT_TYPE ===
                    FORM_AGREEMENT_TYPE.OFF_PLATFORM_REC
                      ? 'Parties have agreed to execute the Confirmation off Platform'
                      : x.arteriaNegotiation?.FORM_AGREEMENT_TYPE}
                </div>
                <div>
                  ${(x) =>
                    getEnumReadableValue(x.contractTypeEnum, x.arteriaNegotiation?.CONTRACT_TYPE)}
                </div>
                <div
                  class="${(x) =>
                    x.isExpiredOrDeleted(x.arteriaNegotiation?.NEGOTIATION_STATUS)
                      ? ''
                      : x.getStatusClassIcon(true)}"
                >
                  ${(x) => x.arteriaNegotiation?.BUYER_COMPANY_NAME}
                </div>
                <div
                  class="${(x) =>
                    x.isExpiredOrDeleted(x.arteriaNegotiation?.NEGOTIATION_STATUS)
                      ? ''
                      : x.getStatusClassIcon(false)}"
                >
                  ${(x) => x.arteriaNegotiation?.SELLER_COMPANY_NAME}
                </div>
                <div>${(x) => x.arteriaNegotiation?.NAME}</div>
                <div>
                  ${(x) => getEnumReadableValue(x.technologyEnum, x.arteriaNegotiation?.TECHNOLOGY)}
                </div>
                <div>${(x) => x.arteriaNegotiation?.STATE_NAME}</div>
                <div>${(x) => x.arteriaNegotiation?.ISO}</div>
                <div>${(x) => x.getDefaultValueIfBlank(x.arteriaNegotiation?.MEGAWATT_AC)}</div>
                <div>${(x) => x.getDefaultValueIfBlank(x.arteriaNegotiation?.BUYERS_FRACTION)}</div>
                <div>
                  ${(x) => x.getDefaultValueIfBlank(x.arteriaNegotiation?.TENOR_START_DATE)}
                </div>
                <div>${(x) => x.getDefaultValueIfBlank(x.arteriaNegotiation?.TENOR_MONTHS)}</div>
                <div>${(x) => x.getDefaultValueIfBlank(x.arteriaNegotiation?.FIXED_PRICE)}</div>
                <div>
                  ${(x) =>
                    x.getDefaultValueIfBlank(
                      getEnumReadableValue(
                        x.applicableRECProgramEnum,
                        x.arteriaNegotiation?.APPLICABLE_REC_PROGRAM
                      )
                    )}
                </div>
                <div>${(x) => x.DEFUALT_REC_VALUE}</div>
              </div>
            </div>

            ${when(
              (x) => !x.isNegotiationDetails,
              html`
                ${when(
                  (x) =>
                    !['EXPIRED', 'BUYER_DELETED', 'SELLER_DELETED', 'FULL_DELETED'].includes(
                      x.arteriaNegotiation?.NEGOTIATION_STATUS
                    ),
                  html<ExecutionDrilldown>`
                    <div class="warning-message">
                      This REC Transaction will be deemed executed, and will become legally binding,
                      in accordance with its terms upon each party clicking "Execute".
                      <br />
                      <br />
                      Note the Execution Timeclock will expire at 5PM EST today. Further, by
                      clicking "Execute", the action cannot be withdrawn unless the Timeclock
                      expires AND the other party does not Execute.
                    </div>
                  `
                )}
                <div
                  class="button-wrapper ${(x) =>
                    ['EXPIRED', 'BUYER_DELETED', 'SELLER_DELETED', 'FULL_DELETED'].includes(
                      x.arteriaNegotiation?.NEGOTIATION_STATUS
                    )
                      ? 'margin-top-long'
                      : ''}"
                >
                  ${when(
                    (x) =>
                      x.arteriaNegotiation?.NEGOTIATION_STATUS === 'PENDING_EXECUTION' ||
                      x.arteriaNegotiation?.NEGOTIATION_STATUS === 'EXECUTED_AWAITING_BUYER' ||
                      x.arteriaNegotiation?.NEGOTIATION_STATUS === 'EXECUTED_AWAITING_SELLER',
                    html<ExecutionDrilldown>`
                      <zero-button
                        :disabled=${(x) => x.disableExecuteBtn()}
                        appearance="danger"
                        @click="${(x) => x.reopenNegotiationConfirm()}"
                      >
                        ${backIcon('start')}Back to Negotiation
                      </zero-button>

                      ${when(
                        (x) => !hasOnlyTransactionAdvisor(x.auth.loggedUserResult),
                        html`
                          <zero-button
                            :disabled=${(x) => x.disableExecuteBtn()}
                            appearance="primary"
                            @click="${(x) => x.executeNegotiation()}"
                          >
                            ${executeIcon('start')}Execute
                          </zero-button>
                        `
                      )}
                    `
                  )}
                  ${when(
                    (x) => x.arteriaNegotiation?.NEGOTIATION_STATUS === 'PENDING_EXECUTION_TIMER',
                    html<ExecutionDrilldown>`
                      <zero-button
                        :disabled=${(x) => x.disableExecuteBtn()}
                        appearance="danger"
                        @click="${(x) => x.reopenNegotiationConfirm()}"
                      >
                        ${backIcon('start')}Back to Negotiation
                      </zero-button>
                      ${when(
                        (x) => !hasOnlyTransactionAdvisor(x.auth.loggedUserResult),
                        html`
                        <zero-button
                          disabled=true
                          appearance="disabled"
                          id="rec-execute-button--pending-execution"
                        >
                          ${executeIcon('start')}Execute
                        </zero-button>
                        <zero-tooltip
                          anchor="rec-execute-button--pending-execution"
                          position="left"
                        >
                          Execution can only occur during Business Hours</br>
                          Monday to Friday from 12AM to 5PM EST
                        </zero-tooltip>
                      `
                      )}
                    `
                  )}
                  ${when(
                    (x) =>
                      ['EXPIRED', 'BUYER_DELETED', 'SELLER_DELETED', 'FULL_DELETED'].includes(
                        x.arteriaNegotiation?.NEGOTIATION_STATUS
                      ),
                    html<ExecutionDrilldown>`
                      <zero-button slot="buttons" appearance="danger" @click="${(x) => x.goToArteriaNegotiationsPage()}">
                        ${backIcon('start')}Return to Contracting Module
                      </zero-button>
                    `
                  )}
                </div>
              `
            )}
          `
        )}
      </div>
    `
  )}
`;
